import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Tooltip,
  TextField,
  IconButton,
  Avatar,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
// import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { Check } from "lucide-react";
import { mavImg, phoImg, phoWBImg } from "../../variables/uris";

// Full-screen onboarding component
// Props:
// - onAirlineSubmit: function(value) called when user presses Enter on the airline/company field
export default function FullScreenOnboarding({
  setCompanyName = (v) => {},
  setProduct = (v) => {},
  setProductBodyType = (v) => {},
}) {
  const [page, setPage] = useState(0);

  // page2 selection
  const [selectedProduct, setSelectedProduct] = useState(null);
  // page3 selection
  const [selectedSeatType] = useState(null);
  // page4 airline/company
  const [airlineName, setAirlineName] = useState("");

  const next = () => setPage((p) => Math.min(p + 1, 3));
  const prev = () => setPage((p) => Math.max(p - 1, 0));

  const containerVariants = {
    enter: (direction) => ({
      y: direction > 0 ? "20vh" : "-20vh",
      opacity: 0,
    }),
    center: { y: 0, opacity: 1 },
    exit: (direction) => ({
      y: direction > 0 ? "-20vh" : "20vh",
      opacity: 0,
    }),
  };

  // sample data for page 2 and 3
  const productLines = [
    {
      id: "Maverick - Widebody Economy/Main Cabin",
      title: "Maverick",
      subtitle: "Widebody Economy/Main Cabin",
      img: mavImg,
      disabled: true,
    },
    {
      id: "Phoenix - Narrowbody First Class",
      title: "Phoenix",
      subtitle: "Narrowbody First Class",
      img: phoImg,
      disabled: false,
    },
    {
      id: "Phoenix - Widebody Premium Economy",
      title: "Phoenix",
      subtitle: "Widebody Premium Economy",
      img: phoWBImg,
      disabled: false,
    },
  ];

  function customDownArrow() {
    return (
      <Box sx={{ position: "relative", height: "25px", width: "25px" }}>
        <ExpandMoreIcon
          className="ExpandMoreIcon"
          sx={{
            position: "absolute",
            right: "0px",
            top: "6px",
            color: "black",
          }}
        />
        <ExpandMoreIcon
          className="ExpandMoreIcon"
          sx={{
            position: "absolute",
            top: "-2px",
            right: "0px",
            color: "#bd2d2f",
          }}
        />
      </Box>
    );
  }
  function customCheckMark() {
    return (
      <Box
        sx={{
          pointerEvents: "none",
          transition: "background-color 0.15s ease",
        }}
        position={"absolute"}
        top={"4.5px"}
        right={"3px"}
        height={"20px"}
        width={"20px"}
        borderRadius={"100px"}
        pointerEvents={"none"}
        backgroundColor={"#CE1126"}
      >
        <Box marginTop={"2px"}>
          <Check size={17} color={"white"}></Check>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "95vh",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Top back arrow */}
      {page > 0 && (
        <IconButton
          onClick={prev}
          aria-label="Back"
          sx={{
            position: "absolute",
            zIndex: 40,
            padding: "0",
            height: "40px",
            width: "40px",
            marginTop: "8px",
            marginLeft: "-20px",
          }}
        >
          <Box>
            <ExpandLessIcon
              className={"ExpandLessIcon"}
              sx={{
                color: "black",
                transition: "0.3s opacity",
                opacity: "0.3",
                "&:hover": {
                  opacity: "1",
                },
              }}
            />
          </Box>
        </IconButton>
      )}

      {/* Animated pages */}
      <AnimatePresence mode="wait" initial={false} custom={page}>
        {page === 0 && (
          <motion.div
            key="page-0"
            custom={1}
            variants={containerVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "tween", duration: 0.3 }}
            style={{ position: "absolute", width: "100%", height: "100%" }}
          >
            <Box
              sx={{
                height: "95%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                px: 3,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Welcome to the Collins Aerospace Aircraft Seat Configurator
              </Typography>
              <Box sx={{ height: "45px" }}></Box>
              <span
                style={{
                  fontSize: "calc(0.93rem + var(--large-screen-font-bump))",
                }}
              >
                Intuitive tool to design seat platforms with real-time impact
                insights
              </span>
            </Box>
          </motion.div>
        )}

        {page === 1 && (
          <motion.div
            key="page-1"
            custom={1}
            variants={containerVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "tween", duration: 0.3 }}
            style={{ position: "absolute", width: "100%", height: "100%" }}
          >
            <Box
              sx={{
                height: "95%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                padding: 0,
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  width: "90%",
                  height: "75%",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    alignItems: "flex-start",
                    fontWeight: "bold",
                    fontSize: "calc(0.81rem + var(--large-screen-font-bump))",
                    letterSpacing: "0.07em",
                    textAlign: "left",
                    paddingBottom: "2vh",
                  }}
                >
                  To begin, please select your seat from the following product
                  lines
                </Box>

                <Grid container sx={{ width: "100%" }}>
                  {productLines.map((p, idx) => {
                    const isSelected = selectedProduct === p.id;
                    const element = (
                      <Box
                        onClick={() => {
                          if (p.disabled) return;
                          setSelectedProduct(p.id);
                          let product = p.id.split(" - ")[0];
                          let productBodyType = p.id.split(" - ")[1];

                          setProduct(product);
                          setProductBodyType(productBodyType);
                        }}
                        sx={{
                          height: "100%",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          position: "relative",
                          cursor: p.disabled ? "default" : "pointer",
                          opacity: p.disabled ? 0.6 : 1,
                          p: 2,
                          userSelect: "none",
                        }}
                      >
                        <Box sx={{ height: "1.5vh" }}></Box>
                        <Avatar
                          variant="rounded"
                          src={p.img}
                          sx={{ height: "40%", width: "auto", mb: 1 }}
                        />
                        <Box sx={{ height: "1.5vh" }}></Box>
                        <Typography variant="subtitle1">{p.title}</Typography>
                        <Box sx={{ height: "1.5vh" }}></Box>
                        <Typography variant="caption">{p.subtitle}</Typography>
                        <Box sx={{ height: "7vh" }}></Box>

                        <Box
                          sx={{
                            opacity: isSelected ? 1 : 0,
                            position: "absolute",
                            top: 0,
                            right: "20px",
                          }}
                        >
                          {customCheckMark()}
                        </Box>
                      </Box>
                    );

                    return (
                      <Grid
                        key={p.id}
                        sx={{
                          borderLeft: idx !== 0 ? "1px solid #e0e0e0" : "none",
                          display: "flex",
                          width: "33.33%",
                          flexGrow: 1,
                        }}
                      >
                        {p.disabled ? (
                          <Tooltip
                            title="This option will be available soon"
                            placement="top"
                          >
                            {element}
                          </Tooltip>
                        ) : (
                          element
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Box>
          </motion.div>
        )}

        {/* {page === 2 && (
          <motion.div
            key="page-2"
            custom={1}
            variants={containerVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "tween", duration: 0.3 }}
            style={{ position: "absolute", width: "100%", height: "100%" }}
          >
            <Box
              sx={{
                height: "95%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                padding: 0,
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  width: "90%",
                  height: "70%",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 4,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    alignItems: "flex-start",
                    fontWeight: "bold",
                    fontSize: "calc(0.81rem + var(--large-screen-font-bump))",
                    letterSpacing: "0.1em",
                    textAlign: "left",
                    paddingBottom: "8vh",
                  }}
                >
                  Please select a double, triple or quad seat type
                </Box>

                <Grid container sx={{ width: "100%" }}>
                  {seatTypes.map((s, idx) => {
                    const isSelected = selectedSeatType === s.id;
                    return (
                      <Grid
                        item
                        xs={4}
                        key={s.id}
                        sx={{
                          borderLeft: idx !== 0 ? "1px solid #e0e0e0" : "none",
                          display: "flex",
                          width: "33.33%", // ✅ ensures each grid takes 1/3
                          flexGrow: 1,
                        }}
                      >
                        <Box
                          onClick={() => setSelectedSeatType(s.id)}
                          sx={{
                            height: "100%",
                            width: "100%",
                            p: 2,
                            cursor: "pointer",
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            alignItems: "flex-start",
                            padding:
                              idx === 0
                                ? "0 20px 0 0"
                                : idx === 1
                                ? "0 20px 0 20px"
                                : "0 0 0 20px",
                          }}
                        >
                          <Box
                            sx={{
                              opacity: isSelected ? 1 : 0,
                              position: "absolute",
                              right: "20px",
                            }}
                          >
                            {customCheckMark()}
                          </Box>
                          <Avatar
                            variant="rounded"
                            src={s.img}
                            sx={{
                              width: "60%",
                              height: "auto",
                              mb: 1,
                              margin: "auto",
                              marginTop: "2vh",
                            }}
                          />

                          <Box sx={{ height: "4vh" }}></Box>

                          <Box
                            sx={{
                              textAlign: "left",
                              width: "100%",
                              fontSize: "calc(0.74rem + var(--large-screen-font-bump))",
                              fontWeight: "bold",
                            }}
                          >
                            {s.title}
                          </Box>

                          <Box sx={{ mt: 1, width: "100%" }}>
                            {s.rows.map(([label, value], i) => (
                              <Box
                                key={i}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  width: "100%",
                                  py: 0.3,
                                }}
                              >
                                <Box sx={{ fontSize: "calc(0.71rem + var(--large-screen-font-bump))" }}>{label}</Box>
                                <Box sx={{ fontSize: "calc(0.71rem + var(--large-screen-font-bump))" }}>{value}</Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Box>
          </motion.div>
        )} */}

        {page === 2 && (
          <motion.div
            key="page-3"
            custom={1}
            variants={containerVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "tween", duration: 0.3 }}
            style={{ position: "absolute", width: "100%", height: "100%" }}
          >
            <Box
              sx={{
                height: "95%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                p: 3,
              }}
            >
              {/* <Typography variant="h4">Almost there</Typography>
              <Box sx={{ height: "1vh" }}></Box> */}
              <Typography
                variant="subtitle1"
                sx={{ mt: 1, mb: 3, fontWeight: "bold" }}
              >
                Please enter your airline or leasing company name
              </Typography>

              <TextField
                className="AirlineNameInput"
                placeholder="Airline/Company Name"
                value={airlineName}
                onChange={(e) => setAirlineName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setCompanyName(e.target.value);
                  }
                }}
                variant="outlined"
                slotProps={{
                  input: {
                    sx: {
                      height: 50,
                      width: 350,
                      borderRadius: 0,
                      backgroundColor: "#f5f5f5",
                      border: airlineName
                        ? "3px solid #CE1126"
                        : "1px solid #e0e0e0",
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      px: 2,
                      fontSize: "calc(1.06rem + var(--large-screen-font-bump))",
                      textAlign: "center",
                    },
                  },
                }}
              />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom forward arrow */}
      <Box
        sx={{
          position: "absolute",
          bottom: 24,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          zIndex: 40,
        }}
      >
        {/* {page < 3 ? ( */}
        <Tooltip
          title={
            page === 1 && !selectedProduct
              ? "Please select a product"
              : // : page === 2 && !selectedSeatType
                // ? "Please select the seat type"
                page === 2 && airlineName === ""
                ? "Please enter your airline/company name"
                : ""
          }
          placement="top"
          disableHoverListener={
            !(
              (page === 1 && !selectedProduct) ||
              // (page === 2 && !selectedSeatType) ||
              (page === 2 && airlineName === "")
            )
          }
        >
          <span>
            <IconButton
              onClick={() => {
                if (
                  (page === 1 && !selectedProduct) ||
                  // (page === 2 && !selectedSeatType) ||
                  (page === 2 && !airlineName)
                ) {
                  return; // Do nothing if requirement not met
                }
                if (page === 2) {
                  setCompanyName(airlineName);
                } else next();
              }}
              aria-label="Next"
              sx={{
                //   position: "absolute",
                bottom: 16,
                left: 0,
                right: 0,
                display: "flex",
                justifyContent: "center",
                zIndex: 40,
                opacity:
                  (page === 1 && !selectedProduct) ||
                  // (page === 2 && !selectedSeatType) ||
                  (page === 2 && !airlineName)
                    ? 0.6
                    : 1,
                cursor:
                  (page === 1 && !selectedProduct) ||
                  // (page === 2 && !selectedSeatType) ||
                  (page === 2 && !airlineName)
                    ? "default"
                    : "pointer",
              }}
            >
              {customDownArrow()}
            </IconButton>
          </span>
        </Tooltip>
        {/* ) : (
          <IconButton
            onClick={() =>
              onAirlineSubmit({
                product: selectedProduct,
                seatType: selectedSeatType,
                airline: airlineName,
              })
            }
            aria-label="Finish"
          >
            {customDownArrow()}
          </IconButton>
        ) */}
        {/* } */}
      </Box>
    </Box>
  );
}
